import { useMemo } from "react";

import debounce from "lodash.debounce";
import { Column, ExternalLink, Row, TextButton, Text, Select } from "@bridgesplit/ui";
import { AppDialog, useAppDialog } from "app/utils";
import { BsMetaUtil, LoopExpanded, isWhirlpoolMetadata, useTokenListMetadataByMints } from "@bridgesplit/abf-react";
import { ClmmPositionExpanded, LoopRouteType, TokenListMetadata } from "@bridgesplit/abf-sdk";
import { ArrowOutwardOutlined } from "@mui/icons-material";

import {
    BalanceDisplay,
    TokenInputWithPrice,
    useBalanceChecker,
    GenericLpPositionSelect,
    FormWithLpPosition,
    TokenImage
} from "../../common";
import { useLoopSetWindTokenAmount } from "./util";
import { useWindContext } from "./WindContext";
import { useLoopContext } from "../LoopContext";
import { WindForm } from "./types";

export default function WindAmount() {
    const {
        form: { inputAmount, inputToken },
        setForm,
        supportedDepositTokens
    } = useWindContext();
    const { loopExpanded } = useLoopContext();
    const handleChange = useLoopSetWindTokenAmount();
    const { getMetadata } = useTokenListMetadataByMints(supportedDepositTokens?.map((t) => t.mint));

    const setValueDebounced = useMemo(() => debounce(handleChange, 500), [handleChange]);

    const metadata = getMetadata(inputToken?.mint);
    const isOrcaPosition = isWhirlpoolMetadata(metadata);

    const { balance, insufficientBalance, isLoading } = useBalanceChecker({
        mint: inputToken?.mint,
        amount: inputAmount,
        skip: isOrcaPosition
    });

    function handleInputTokenChange(token: TokenListMetadata) {
        setForm((prev) => ({
            ...prev,
            inputToken: token,
            inputAmount: undefined,
            orcaPosition: undefined
        }));
    }

    const setToken = {
        tokens: supportedDepositTokens,
        setToken: handleInputTokenChange,
        header: ""
    };

    return (
        <Column spacing={0.5}>
            <Row spaceBetween>
                <Text color="caption" variant="body2">
                    You deposit
                </Text>
                <GetLoopWindTokenButton loopExpanded={loopExpanded} />
            </Row>

            {/**Select either orca or usd as the input only rendered when orca because takes up full input area like borrow page */}
            {isOrcaPosition && inputToken && (
                <NftSelectDropdown
                    metadata={inputToken}
                    tokens={supportedDepositTokens || []}
                    setToken={handleInputTokenChange}
                />
            )}

            {/**Select either usd as the input only rendered when not orca because usd input shows price amount like borrow page */}
            {!isOrcaPosition && (
                <TokenInputWithPrice
                    metadata={inputToken}
                    selectToken={supportedDepositTokens && supportedDepositTokens.length > 1 ? setToken : null}
                    value={inputAmount}
                    setValue={setValueDebounced}
                    supportedDepositTokens={supportedDepositTokens}
                    loading={isLoading}
                />
            )}

            {/**This is where you select a specific orca position */}
            {isOrcaPosition && <WindLpPositionSelect />}
            {!isOrcaPosition && (
                <BalanceDisplay
                    balance={balance}
                    insufficientBalance={insufficientBalance}
                    isLoading={isLoading}
                    setMax={handleChange}
                    metadata={inputToken}
                />
            )}
        </Column>
    );
}

function WindLpPositionSelect() {
    const { form, setForm, whirlpoolPositions } = useWindContext();
    const { loopExpanded } = useLoopContext();

    const collateralMint = loopExpanded?.collateralToken.mint;

    if (!collateralMint) return null;

    return (
        <GenericLpPositionSelect<WindForm & FormWithLpPosition>
            collateralMint={collateralMint}
            labelColor="caption"
            form={form}
            setForm={setForm}
            positions={whirlpoolPositions}
            onPositionChange={(position: ClmmPositionExpanded) => {
                return {
                    ...form,
                    inputAmount: position.totalPrice,
                    clmmPosition: position
                };
            }}
        />
    );
}

function GetLoopWindTokenButton({ loopExpanded }: { loopExpanded: LoopExpanded | undefined }) {
    const { open } = useAppDialog();
    const {
        oraclePrices,
        form: { inputToken }
    } = useWindContext();

    const isWhirlpoolPoolSelectedInput = loopExpanded?.collateralToken.mint === inputToken?.mint;
    const jupLink = `https://jup.ag/swap?sell=${loopExpanded?.principalToken.mint}&buy=${loopExpanded?.inputToken.mint}`;

    const orcaLink = isWhirlpoolPoolSelectedInput
        ? `https://orca.so/pools/${loopExpanded?.collateralToken.mint}`
        : `https://jup.ag/swap/SOL-${BsMetaUtil.getSymbol(loopExpanded?.inputToken)}`;

    if (!loopExpanded) return null;

    switch (loopExpanded.loopVault.routeType) {
        case LoopRouteType.Meteora:
            return (
                <TextButton
                    color="primary"
                    variant="body2"
                    onClick={() => open(AppDialog.LoopExternalTransfers, { loopExpanded })}
                >
                    Get {BsMetaUtil.getSymbol(loopExpanded?.inputToken)}
                </TextButton>
            );
        case LoopRouteType.Jup:
            return (
                <ExternalLink target="_blank" rel="noopener" href={jupLink}>
                    <TextButton color="primary" variant="body2">
                        Get {BsMetaUtil.getSymbol(loopExpanded?.inputToken)}
                        <ArrowOutwardOutlined />
                    </TextButton>
                </ExternalLink>
            );
        case LoopRouteType.Stabble:
            return (
                <ExternalLink
                    target="_blank"
                    rel="noopener"
                    href={`https://app.stabble.org/pools/deposit/?poolId=${
                        oraclePrices?.get(loopExpanded?.collateralToken.mint)?.oracleAccount
                    }`}
                >
                    <TextButton color="primary" variant="body2">
                        Get {BsMetaUtil.getSymbol(loopExpanded?.inputToken)}
                        <ArrowOutwardOutlined />
                    </TextButton>
                </ExternalLink>
            );
        case LoopRouteType.Exponent:
            return (
                <ExternalLink
                    target="_blank"
                    rel="noopener"
                    href={`https://www.exponent.finance/income/${loopExpanded?.collateralToken.name.replace(
                        "Exponent ",
                        ""
                    )}?preserveScroll=true`}
                >
                    <TextButton color="primary" variant="body2">
                        Get {BsMetaUtil.getSymbol(loopExpanded?.inputToken)}
                        <ArrowOutwardOutlined />
                    </TextButton>
                </ExternalLink>
            );
        case LoopRouteType.Ratex:
            return (
                <ExternalLink target="_blank" rel="noopener" href={`https://app.rate-x.io/earn/fixed-yield`}>
                    <TextButton color="primary" variant="body2">
                        Get PT-{BsMetaUtil.getSymbol(loopExpanded?.inputToken)}
                        <ArrowOutwardOutlined />
                    </TextButton>
                </ExternalLink>
            );
        case LoopRouteType.Flash:
            return (
                <ExternalLink target="_blank" rel="noopener" href={`https://beast.flash.trade/earn`}>
                    <TextButton color="primary" variant="body2">
                        Get {BsMetaUtil.getSymbol(loopExpanded?.inputToken)}
                        <ArrowOutwardOutlined />
                    </TextButton>
                </ExternalLink>
            );
        case LoopRouteType.Adrena:
            return (
                <ExternalLink target="_blank" rel="noopener" href={`https://app.adrena.xyz/buy_alp`}>
                    <TextButton color="primary" variant="body2">
                        Get {BsMetaUtil.getSymbol(loopExpanded?.inputToken)}
                        <ArrowOutwardOutlined />
                    </TextButton>
                </ExternalLink>
            );
        case LoopRouteType.Orca:
            return (
                <ExternalLink target="_blank" rel="noopener" href={orcaLink}>
                    <TextButton color="primary" variant="body2">
                        Get {BsMetaUtil.getSymbol(inputToken)}
                        <ArrowOutwardOutlined />
                    </TextButton>
                </ExternalLink>
            );
        default:
            return null;
    }
}

export function NftSelectDropdown({
    metadata,
    tokens,
    setToken
}: {
    metadata: TokenListMetadata;
    tokens: TokenListMetadata[];
    setToken: (token: TokenListMetadata) => void;
}) {
    const options = tokens.map((token) => ({
        value: token.mint,
        label: (
            <Row spacing={1}>
                <TokenImage metadata={token} size="sm" />
                <Text>{BsMetaUtil.getName(token)}</Text>
            </Row>
        )
    }));

    return (
        <Select
            renderValue={() => (
                <Row spacing={1}>
                    <TokenImage metadata={metadata} size="sm" />
                    <Text>{BsMetaUtil.getName(metadata)}</Text>
                </Row>
            )}
            value={metadata.mint}
            setValue={(mint) => {
                const selectedToken = tokens.find((t) => t.mint === mint);
                if (selectedToken) {
                    setToken(selectedToken);
                }
            }}
            options={options}
        />
    );
}
